'use client';

import type {CSSProperties} from 'react';

import {useI18n} from '@udemy-v2/i18n';
import {Icon} from '@udemy-v2/icons';
import {Button} from '@udemy-v2/react-core-components';

import {getAssetBase} from '@/lib/asset-base';

import styles from './testimonial-card.module.scss';

const ASSET_BASE = getAssetBase('instructor-testimonials/v1');

/** Tint the card is drawn on. Each maps to a `--color-<tone>-150` step, see the module. */
export type TestimonialTone = 'green' | 'purple' | 'orange';

export interface Testimonial {
    /**
     * Stable identifier, also the basename of the card's art asset
     * (`<slug>-desktop.webp` / `<slug>-mobile.webp`).
     */
    slug: string;
    /** The instructor's words, as one paragraph. */
    quote: string;
    /** The instructor's full name. */
    name: string;
    /** Short subject line under the name, e.g. "Canva & Design". */
    role: string;
    /** CTA label — Figma uses the instructor's first name ("More about Frank"). */
    ctaLabel: string;
    tone: TestimonialTone;
    /**
     * Width of the quote's text box in Figma pixels, at the card's 1291px design width.
     * Figma sizes each quote individually (374/432/542) and that is what decides where the
     * lines break, so it is per-testimonial rather than one shared cap. Defaults to 542.
     */
    quoteInlineSize?: number;
    /**
     * Link for the CTA. Renders as a plain button while unset — the design does not
     * name a destination, so the real profile URLs still have to be supplied.
     */
    profileUrl?: string;
    /**
     * The instructor's user id and their community's id, for the CTA's analytics event
     * (`instructor.id` / `instructorCommunity.id` on `PromotionSelected`).
     *
     * Analytics only — nothing rendered reads them. Optional because these entries are
     * hardcoded editorial content rather than fetched: an entry without them renders
     * identically and simply produces no event. The joint testimonial naming two
     * instructors has no single id to carry and is expected to leave them unset.
     */
    instructorId?: string;
    communityId?: string;
}

export interface TestimonialCardProps extends Testimonial {
    /**
     * Load the art eagerly. Set on the slide the carousel opens centred on — the design opens
     * on the second card, so this is not necessarily the first one in the list.
     */
    isInitiallyVisible?: boolean;
    /**
     * Called when the learner clicks the CTA, before the browser leaves the page. Only
     * wired on the linked variant: the placeholder button navigates nowhere, so a click
     * on it is not a click-through.
     */
    onSelected?: () => void;
}

/**
 * One instructor testimonial — Figma nodes 6401:28620 / 6401:29056 / 6401:29492 (desktop,
 * the 2026-09-03 refresh) and 5260:160548 (mobile), file `mitwWlKOuf1pyo4gUCLEBY`.
 *
 * A tinted rounded card: quote glyph, the quote, the instructor's name and subject, and
 * a CTA to their profile, beside a cut-out photo of them over decorative brushstroke
 * art. On mobile the same column sits at the top and the art moves to the bottom edge.
 *
 * The art is a single flat asset per card and breakpoint, composited from Figma's art
 * layer (which bakes the card tint behind it) and the instructor's transparent cut-out —
 * the brushstrokes are rotated, masked raster layers that cannot be reproduced in CSS.
 * See `docs/implementation-plan.md` for how those exports are built.
 *
 * Presentational: every string arrives through props so the copy stays literal
 * `gettext(…)` calls in the parent.
 */
export function TestimonialCard({
    slug,
    quote,
    name,
    role,
    ctaLabel,
    tone,
    quoteInlineSize,
    profileUrl,
    isInitiallyVisible = false,
    onSelected,
}: TestimonialCardProps) {
    const {gettext} = useI18n();

    // Left unset when the testimonial does not carry one, so the module's default applies
    // rather than this restating it.
    const quoteStyle = quoteInlineSize
        ? ({'--quote-figma-inline-size': quoteInlineSize} as CSSProperties)
        : undefined;

    return (
        <figure className={styles['card']} data-tone={tone} data-purpose="testimonial-card">
            {/* Decorative: the quote and attribution carry the meaning, and the
                instructor is named in the text beside their photo. */}
            <picture className={styles['art']}>
                <source media="(max-width: 900px)" srcSet={`${ASSET_BASE}/${slug}-mobile.webp`} />
                <img
                    src={`${ASSET_BASE}/${slug}-desktop.webp`}
                    alt=""
                    loading={isInitiallyVisible ? 'eager' : 'lazy'}
                    decoding="async"
                />
            </picture>

            <div className={styles['content']}>
                <span className={styles['quote-icon']}>
                    <Icon glyph="quote" label={false} size="xxlarge" />
                </span>

                <div className={styles['body']}>
                    <blockquote className={styles['quote']} style={quoteStyle}>
                        <p>{quote}</p>
                    </blockquote>

                    <figcaption className={styles['meta']}>
                        <span className={styles['attribution']}>
                            <span className={styles['name']}>{name}</span>
                            <span className={styles['role']}>{role}</span>
                        </span>
                        {profileUrl ? (
                            <Button
                                componentClass="a"
                                href={profileUrl}
                                udStyle="secondary"
                                size="small"
                                onClick={onSelected}
                            >
                                {ctaLabel}
                            </Button>
                        ) : (
                            /* No destination in the design yet; keeps the drawn layout
                               until real profile URLs are wired in. */
                            <Button
                                udStyle="secondary"
                                size="small"
                                aria-label={`${ctaLabel} (${gettext('coming soon')})`}
                            >
                                {ctaLabel}
                            </Button>
                        )}
                    </figcaption>
                </div>
            </div>
        </figure>
    );
}
